/**
 * MatterPricingPanel - pricing is EDITED here, in the Economics tab.
 *
 * The pricing card on Details owned both the reading and the writing, which
 * put a money operation on the tab that describes the matter and left the
 * Economics tab - the tab about money - able only to report. Details is now
 * view-only with a link across, and every pricing write happens in this side
 * panel, the way the rest of the matter's operations do.
 *
 * The save is the card's original one, unchanged: the same PascalCase payload,
 * the same audit-log warning, the same confirmation when the model or currency
 * changes on a matter that already has logged time, and the same snake_case
 * patch handed back so the page re-renders without a refetch.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiWarningLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import { editMatter } from '../../../store/actions/Matters/MatterActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import { fetchMatterEconomics } from '../../../store/actions/economicsActions';
import Button from '../../shared/TailwindComponents/Button';
import MatterPricingFields from './MatterPricingFields';
import {
  MatterPricingValue,
  PRICING_MODEL_LABEL_KEYS,
  requiresFixedFee,
  requiresContingency,
  getMatterPricingErrors,
} from './economicsConstants';
import type { PricingModel } from '../../../types/economics';

export interface MatterPricingPanelProps {
  matterData: any;
  /** Already-resolved lowercase currency, so the form cannot seed a different one. */
  currency: string;
  /** Already-resolved budget (matter blob, else the economics rollup). */
  budgetAmount: number | null;
  /** Receives the saved fields in the matter's own snake_case spelling. */
  onSaved?: (patch: Record<string, any>) => void;
  onClose: () => void;
}

const MatterPricingPanel: React.FC<MatterPricingPanelProps> = ({
  matterData,
  currency,
  budgetAmount,
  onSaved,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const initial = useMemo<MatterPricingValue>(() => ({
    pricing_model: (matterData?.pricing_model as PricingModel) || 'hourly',
    currency,
    fixed_fee_amount: matterData?.fixed_fee_amount != null ? String(matterData.fixed_fee_amount) : '',
    budget_amount: budgetAmount != null ? String(budgetAmount) : '',
    contingency_pct: matterData?.contingency_pct != null ? String(Number(matterData.contingency_pct) * 100) : '',
  }), [matterData, currency, budgetAmount]);

  const [value, setValue] = useState<MatterPricingValue>(initial);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  // Whether the matter has logged time - gates the pricing-change confirmation
  // (#2247). MatterData carries no time-entry count, so the economics summary's
  // total_hours is read when the panel opens.
  const [hasTimeEntries, setHasTimeEntries] = useState(false);

  React.useEffect(() => {
    let alive = true;
    if (!matterData?.uuid) return () => { alive = false; };
    dispatch(fetchMatterEconomics(matterData.uuid))
      .then((res) => { if (alive) setHasTimeEntries(Number(res?.total_hours || 0) > 0); })
      .catch(() => { if (alive) setHasTimeEntries(false); });
    return () => { alive = false; };
  }, [dispatch, matterData?.uuid]);

  /** Push the pricing change to the BE and surface any non-fatal warnings[]. */
  const doSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, any> = { PricingModel: value.pricing_model };
      /*
        Only send a currency we actually have. An empty picker used to post
        `Currency: ''`, which is not a currency - the column ends up blank and
        the card is back to "—" with the save having reported success.
      */
      const nextCurrency = (value.currency || '').trim().toLowerCase();
      if (nextCurrency) payload.Currency = nextCurrency;
      if (requiresFixedFee(value.pricing_model) && value.fixed_fee_amount.trim() !== '') {
        payload.FixedFeeAmount = String(Number(value.fixed_fee_amount));
      }
      /*
        Budget rides the same save. Sent on EVERY pricing model, and sent as
        null when cleared - omitting it would make the field write-once, with
        no way to remove a budget set by mistake.
      */
      const budget = value.budget_amount.trim();
      payload.BudgetAmount = budget === '' ? null : String(Number(budget));
      if (requiresContingency(value.pricing_model) && value.contingency_pct.trim() !== '') {
        payload.ContingencyPct = String(Number(value.contingency_pct) / 100);
      }

      const result: any = await dispatch(editMatter(matterData.uuid, payload));
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'success',
        message: t('Pricing updated'),
        subMsg: t('This change has been recorded in the matter audit log.'),
        duration: 4000,
      }));
      // BE returns a non-fatal warnings[] on the edit response (revenue impact,
      // mixed currencies). Surface each as its own toast.
      const warnings = result?.warnings;
      if (Array.isArray(warnings)) {
        warnings.forEach((w: unknown) =>
          dispatch(showFeedbackModal({ showModal: true, modalType: 'warning', message: String(w), duration: 6000 })),
        );
      }
      /*
        The patch is keyed the way the MATTER is, not the way the wire is —
        the payload above is PascalCase for the API, but every card reads
        `pricing_model` / `currency` / `budget_amount`.
      */
      onSaved?.({
        pricing_model: value.pricing_model,
        ...(nextCurrency ? { currency: nextCurrency } : {}),
        budget_amount: budget === '' ? null : Number(budget),
        ...(payload.FixedFeeAmount !== undefined ? { fixed_fee_amount: Number(payload.FixedFeeAmount) } : {}),
        ...(payload.ContingencyPct !== undefined ? { contingency_pct: Number(payload.ContingencyPct) } : {}),
      });
      onClose();
    } catch {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: t('Failed to update pricing'), duration: 4000 }));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (Object.keys(getMatterPricingErrors(value)).length > 0) { setShowErrors(true); return; }

    const oldModel = (matterData?.pricing_model as PricingModel) || 'hourly';
    const oldCurrency = currency;
    const modelChanged = value.pricing_model !== oldModel;
    const currencyChanged = value.currency.toLowerCase() !== oldCurrency;

    // Confirm before changing pricing model / currency on a matter that already
    // has logged time, since it changes how revenue is calculated (#2247).
    if ((modelChanged || currencyChanged) && hasTimeEntries) {
      const modelLabel = (m: PricingModel) => t(PRICING_MODEL_LABEL_KEYS[m]);
      const ccy = (c: string) => (c || '—').toUpperCase();
      let what: string;
      let oldDisp: string;
      let newDisp: string;
      if (modelChanged && currencyChanged) {
        what = t('pricing model and currency');
        oldDisp = `${modelLabel(oldModel)} / ${ccy(oldCurrency)}`;
        newDisp = `${modelLabel(value.pricing_model)} / ${ccy(value.currency)}`;
      } else if (modelChanged) {
        what = t('pricing model');
        oldDisp = modelLabel(oldModel);
        newDisp = modelLabel(value.pricing_model);
      } else {
        what = t('currency');
        oldDisp = ccy(oldCurrency);
        newDisp = ccy(value.currency);
      }
      dispatch(showModal({
        showModal: true,
        type: 'confirmation',
        title: t('Change pricing for this matter?'),
        message: t(
          'This matter already has logged time. Changing the pricing {{what}} from {{old}} to {{new}} will affect how revenue is calculated. The change will be audit-logged.',
          { what, old: oldDisp, new: newDisp },
        ),
        variant: 'warning',
        icon: <PiWarningLight className="w-6 h-6 text-warning-600" />,
        confirmLabel: t('Yes, change'),
        onConfirm: () => { dispatch(hideModal()); doSave(); },
        onClose: () => dispatch(hideModal()),
      }));
      return;
    }

    doSave();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto custom-scrollbar-main p-5 space-y-4">
        <div className="flex items-start gap-2 rounded-md border border-warning-300 bg-warning-50 dark:border-warning-700/60 dark:bg-warning-900/20 p-3">
          <PiWarningLight className="h-5 w-5 text-warning-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-warning-800 dark:text-warning-300">
            {t('Changes to pricing will be audit-logged and may affect existing revenue calculations.')}
          </p>
        </div>
        <MatterPricingFields value={value} onChange={setValue} showErrors={showErrors} />
      </div>
      <div className="border-t border-hairline-soft px-5 py-4 flex justify-end gap-2">
        <Button variant="neutral" size="small" onClick={onClose} disabled={saving}>{t('Cancel')}</Button>
        <Button variant="primary" size="small" onClick={save} disabled={saving}>
          {saving ? t('Saving...') : t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default MatterPricingPanel;
